/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { tagName } from '@ember-decorators/component';
import { action, computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { debounce } from '@ember/runloop';
import config from 'cio/config/environment';
import isDestroyed from 'cio/utils/is-destroyed';
import { ConditionEditorSubLayout } from 'cio/utils/condition-editor';
import { ComparisonOperator } from 'cio/utils/operators';
import { ConditionType } from 'cio/utils/condition-types';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import { isRelativeTimestampOperator, ago, fromNow } from 'cio/app-models/conditions/attribute';
import { isEmpty } from '@ember/utils';

const ERROR_SOURCE_NAME_MAP = {
  'segments.segment.overview.edit': 'Segment Edit Page',
  'segments.new': 'Segment Setup Page',
  'campaigns.campaign.setup.workflow.actions': 'True-False Branch',
  'broadcasts.newsletter.setup.recipients.index': 'Newsletter Recipients Setup Page',
  'people.list.index': 'People Page',
};

@tagName('')
export default class Value extends Component {
  @service environment;
  @service autosuggest;
  @service analytics;
  @service router;
  @service moment;

  // external API
  condition = null;

  disabled = false;
  canCompareValues = false;
  isTimestamp = false;
  showRelativeTimestampValue = false;
  timeDirections = null;
  showTwoValues = false;
  isTwoValuesTimestamp = false;
  selectedTimestampsDates = null;
  showValue = false;
  timestampInequalityOperatorVariants = null;
  selectedTimestampInequalityOperatorVariant = null;
  placeholder = null;
  autosuggestValues = false;
  autosuggestValueOverrides = {};
  isLoadingAutosuggestValueOverrides = false;
  valueInputClassNames = '';
  onTimestampValueSelect() {}
  timestampValueSelectionFinished() {}
  timestampInequalityVariantChange() {}
  onValueChange() {}
  size = null;
  variation = null;
  fluid = false;
  widthClass = null;
  campaign = null;
  triggerEvent = null;

  // internal API
  @computed('placeholder', 'canCompareValues', 'condition.comparisonOperator')
  get _placeholder() {
    if (this.placeholder) {
      return this.placeholder;
    }

    if (this.canCompareValues) {
      if (['value', 'bw'].includes(this.condition.comparisonOperator)) {
        return 'value';
      }
      if (this.condition.comparisonOperator === ComparisonOperator.Event) {
        if (this.campaign?.isWebhookTriggered || this.condition.isForeignEventFilterAttribute) {
          return 'trigger attribute';
        }
        return 'event_attribute_name';
      }

      if (this.condition.comparisonOperator === ComparisonOperator.Customer) {
        return 'profile attribute';
      }
    }

    if (this.condition.type === 'event_attribute') {
      if (this.campaign?.isWebhookTriggered) {
        return 'webhook_attribute_value';
      }
      return 'event_attribute_value';
    }

    return 'profile attribute value';
  }

  @computed('variation', 'subLayout')
  get isStackedLayout() {
    return this.variation === 'compact' && this.subLayout !== ConditionEditorSubLayout.Inline;
  }

  _hasObserver = false;
  // Prevent the same analytics event from firing when errors are re-computed
  trackedErrors = [];

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    if (this.autosuggestValues && this.isAutosuggestEnabledConditionType()) {
      this.loadAutosuggestValues();

      if (!this._hasObserver) {
        /* eslint-disable spellcheck/spell-checker */
        this.addObserver('condition.name', this, 'debounceLoadAutosuggestValues');
        this.addObserver('condition.comparisonOperator', this, 'debounceLoadAutosuggestValues');
        /* eslint-enable spellcheck/spell-checker */
        this.set('_hasObserver', true);
      }
    }
  }

  willDestroyElement() {
    super.willDestroyElement(...arguments);

    /* eslint-disable spellcheck/spell-checker */
    this.removeObserver('condition.name', this, 'debounceLoadAutosuggestValues');
    this.removeObserver('condition.comparisonOperator', this, 'debounceLoadAutosuggestValues');
    /* eslint-enable spellcheck/spell-checker */
    this.set('_hasObserver', true);
  }

  attributeValuesTaskInstance = null;

  @computed('autosuggestValueOverrides', 'attributeValuesTaskInstance.value', 'condition.comparisonOperator')
  get attributeValues() {
    let operator = this.condition.comparisonOperator;
    if (this.autosuggestValueOverrides?.[operator] != null) {
      return this.autosuggestValueOverrides?.[operator];
    }

    if (this.attributeValuesTaskInstance != null) {
      return this.attributeValuesTaskInstance.value;
    }

    return [];
  }

  @computed('isLoadingAutosuggestValueOverrides', 'attributeValuesTaskInstance.isRunning')
  get isLoadingAttributeValues() {
    return this.isLoadingAutosuggestValueOverrides || this.attributeValuesTaskInstance?.isRunning;
  }

  loadAutosuggestValues() {
    if (isDestroyed(this) || (this.campaign?.isWebhookTriggered ?? false)) {
      return;
    }
    let { event, comparisonOperator, name } = this.condition;

    if (this.condition instanceof EventAttributeCondition && Boolean(this.condition.isForeignEventFilterAttribute)) {
      if (comparisonOperator === ComparisonOperator.Event) {
        if (this.triggerEvent == null) {
          this.set('attributeValuesTaskInstance', null);
        } else {
          this.set('attributeValuesTaskInstance', this.autosuggest.loadEventKeys.perform(this.triggerEvent));
        }
      } else if (!isEmpty(this.condition.event) && !isEmpty(this.condition.name)) {
        this.set('attributeValuesTaskInstance', this.autosuggest.loadEventAttributeValues.perform(event, name));
      }
      return;
    }

    if (!isEmpty(this.condition.name)) {
      if (this.isAutosuggestEnabledConditionType()) {
        if (this.condition.comparisonOperator === 'event_attribute' && !isEmpty(this.condition.event)) {
          this.set('attributeValuesTaskInstance', this.autosuggest.loadEventKeys.perform(this.condition.event));
        } else if (this.condition.type === 'event_attribute' && !isEmpty(this.condition.event)) {
          this.set(
            'attributeValuesTaskInstance',
            this.autosuggest.loadEventAttributeValues.perform(this.condition.event, this.condition.name),
          );
        } else if (this.condition.type === 'device_attribute') {
          this.set(
            'attributeValuesTaskInstance',
            this.autosuggest.loadDeviceAttributeValues.perform(this.condition.name),
          );
        }
      }
    } else {
      this.set('attributeValuesTaskInstance', null);
    }
  }

  isAutosuggestEnabledConditionType() {
    let autosuggestEnabledTypes = [
      ConditionType.EventAttribute,
      ConditionType.CollectionAttribute,
      ConditionType.Attribute,
      ConditionType.DeviceAttribute,
      ConditionType.ForeignEventAttribute,
    ];
    return autosuggestEnabledTypes.includes(this.condition.type);
  }

  debounceLoadAutosuggestValues() {
    debounce(this, this.loadAutosuggestValues, config.APP.autosuggestDebounceDuration);
  }

  @computed('variation')
  get _itemClass() {
    if (this.isStackedLayout) {
      return 'flex-1 min-w-0';
    }

    return '';
  }

  @computed('variation', 'subLayout', 'isTwoValuesTimestamp')
  get _twoValuesContainerClass() {
    if (this.isTwoValuesTimestamp && this.subLayout !== 'inline') {
      return 'flex flex-col items-stretch';
    }

    return 'flex';
  }

  @computed('variation', 'subLayout', 'isTwoValuesTimestamp')
  get _twoValuesItemClass() {
    if (this.subLayout !== 'inline') {
      return 'flex-1 min-w-0';
    }

    return '';
  }

  @computed('moment.currentTimestamp', 'condition.{timestampValue,operator,timeDirection}')
  get _absoluteTimestampValue() {
    let { operator, timestampValue, timeDirection } = this.condition;

    if (!isRelativeTimestampOperator(operator)) {
      return null;
    }

    if (timestampValue == null) {
      return null;
    }

    let timestampValueInSeconds = parseInt(timestampValue) * 24 * 60 * 60;

    let now = this.moment.currentTimestamp;

    if (timeDirection === fromNow) {
      now = now + timestampValueInSeconds;
    } else if (timeDirection === ago) {
      now = now - timestampValueInSeconds;
    }

    return new Date(now * 1000);
  }

  @computed('showRelativeTimestampValue', 'condition.operator')
  get _relativeTimestampPreposition() {
    if (!this.showRelativeTimestampValue) {
      return null;
    }

    if (this.condition.operator === 'timestamp_lt') {
      return 'before';
    } else {
      return 'after';
    }
  }

  @readOnly('environment.sentAttributes')
  availableAttributes;

  @action
  trackErrorChange(_, [errors]) {
    if (errors?.length && !this.trackedErrors.includes(errors[0])) {
      this.set('trackedErrors', [...this.trackedErrors, errors[0]]);

      this.analytics.track('user', 'error_encountered', {
        currentUrl: this.router.currentURL,
        message: errors[0],
        source: ERROR_SOURCE_NAME_MAP[this.router.currentRouteName],
      });
    }
  }

  @action
  setValue(value) {
    this.condition.set('value', value);
    this.onValueChange(value);
  }
}
